import { createSlice } from '@reduxjs/toolkit';
import { tokenStorage } from '../utils/tokenStorage';

// Helper function to remove non-serializable values (functions, class instances, etc.)
const makeSerializable = (obj) => {
    if (obj === null || obj === undefined) return obj;
    if (typeof obj !== 'object') return obj;
    if (Array.isArray(obj)) {
        return obj.map(item => makeSerializable(item));
    }
    const serializable = {};
    for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            const value = obj[key];
            // Skip functions and other non-serializable types
            if (typeof value === 'function') continue;
            if (value instanceof Date) {
                serializable[key] = value.toISOString();
            } else if (typeof value === 'object' && value !== null) {
                serializable[key] = makeSerializable(value);
            } else {
                serializable[key] = value;
            }
        }
    }
    return serializable;
};

const userSlice = createSlice({
    name: 'user',
    initialState: {
        value: 0,
        jwt: tokenStorage.getAccessToken(), // Use new token storage
        userDetails: {},
        coinsCount: 0
    },
    reducers: {
        setJwtToken: (state, action) => {
            // If we receive a token, save it (for backward compatibility)
            // Prefer tokenStorage.saveAccessToken() when persisting full session + expiry
            if (action?.payload?.token) {
                const token = action.payload.token;
                localStorage.setItem("jwt", token);
                state.jwt = token;
            }
        },
        setUserDetails: (state, action) => {
            state.userDetails = action?.payload;
        },
        setTutorialCompleted: (state, action) => {
            if (state.userDetails && typeof action?.payload === "boolean") {
                state.userDetails = { ...state.userDetails, hasCompletedTutorial: action.payload };
            }
        },
        setCoinsCount: (state, action) => {
            state.coinsCount = action?.payload?.coinsCount
        },
        logoutUser: (state) => {
            // tokenStorage.clearTokens() should be called before this
            // This just updates the Redux state
            state.jwt = "";
            state.userDetails = {};
            state.coinsCount = 0;
        },
    },
});

export const { setJwtToken, setUserDetails, setTutorialCompleted, setCoinsCount, logoutUser } = userSlice.actions;
export default userSlice.reducer;

// Slice for storing cleaned datasets by useCaseId
const cleanedDatasetsSlice = createSlice({
    name: 'cleanedDatasets',
    initialState: {
        cleanedDatasets: {}
    },
    reducers: {
        setCleanedDataset: (state, action) => {
            const { useCaseId, dataset, dataTypes } = action.payload;
            state.cleanedDatasets[useCaseId] = {
                dataset,
                dataTypes,
                timestamp: new Date().toISOString()
            };
        },
        clearCleanedDataset: (state, action) => {
            const { useCaseId } = action.payload;
            delete state.cleanedDatasets[useCaseId];
        },
        clearAllCleanedDatasets: (state) => {
            state.cleanedDatasets = {};
        }
    },
});

export const { setCleanedDataset, clearCleanedDataset, clearAllCleanedDatasets } = cleanedDatasetsSlice.actions;
export const cleanedDatasetsReducer = cleanedDatasetsSlice.reducer;

// Slice for storing completed charts by problemId and chartType
const completedChartsSlice = createSlice({
    name: 'completedCharts',
    initialState: {
        completedCharts: {}
    },
    reducers: {
        setCompletedChart: (state, action) => {
            const { problemId, chartType, chartConfig } = action.payload;
            if (!state.completedCharts[problemId]) {
                state.completedCharts[problemId] = {};
            }
            // Ensure we only store serializable data (no functions, no complex objects with methods)
            // Explicitly extract only the fields we need and make them serializable
            const serializableConfig = makeSerializable({
                mapping: chartConfig.mapping,
                visualOptions: chartConfig.visualOptions,
                dataset: chartConfig.dataset,
                dataTypes: chartConfig.dataTypes,
                chartType: chartConfig.chartType || chartType,
                chartName: chartConfig.chartName || chartConfig.chart?.metadata?.name || chartType,
                timestamp: new Date().toISOString()
            });
            state.completedCharts[problemId][chartType] = serializableConfig;
        },
        clearCompletedChart: (state, action) => {
            const { problemId, chartType } = action.payload;
            if (state.completedCharts[problemId] && state.completedCharts[problemId][chartType]) {
                delete state.completedCharts[problemId][chartType];
            }
        },
        clearAllCompletedChartsForProblem: (state, action) => {
            const { problemId } = action.payload;
            delete state.completedCharts[problemId];
        }
    },
});

export const { setCompletedChart, clearCompletedChart, clearAllCompletedChartsForProblem } = completedChartsSlice.actions;
export const completedChartsReducer = completedChartsSlice.reducer;

// Slice for storing raw (uncleaned) datasets prefetched from CloudFront
const rawDatasetsSlice = createSlice({
    name: 'rawDatasets',
    initialState: {
        datasets: {} // { [problemId]: { data: [...], dataSource: "url", timestamp: "..." } }
    },
    reducers: {
        setRawDataset: (state, action) => {
            const { problemId, dataset, dataSource } = action.payload;
            state.datasets[problemId] = {
                data: dataset,
                dataSource,
                timestamp: new Date().toISOString()
            };
        },
        clearRawDataset: (state, action) => {
            const { problemId } = action.payload;
            delete state.datasets[problemId];
        },
        clearAllRawDatasets: (state) => {
            state.datasets = {};
        }
    },
});

export const { setRawDataset, clearRawDataset, clearAllRawDatasets } = rawDatasetsSlice.actions;
export const rawDatasetsReducer = rawDatasetsSlice.reducer;